/**
 * To-dos in the Full response reader — `[TODO: …]` notes and `[ATT: …]`
 * attachment placeholders — styled as the editor styles them.
 *
 * The editor paints each token with annotationDecorationPlugin: an inline span
 * carrying `annotation-chip annotation-chip-todo` (or `-att`) and a
 * `data-annotation-type` of `todo` / `attachment` (styled in src/index.css).
 * The reader renders stored HTML instead of an editor, so it wraps the same
 * tokens in the same spans here, matching per text node exactly as the plugin
 * does — a token broken across marks is left as plain text in both places, and
 * an `[ATT: …]` inside a note stays part of the note.
 *
 * Runs on the RAW html, before `sanitizeResponseHtml`, so DOMPurify stays the
 * last thing to touch the markup. Parsing happens in a DOMParser document,
 * which is inert: nothing in it runs or loads.
 */

import { ATT_REGEX, TODO_REGEX } from '@/lib/annotations'

const PATTERNS = [
  // TODO first, as in the plugin: its payload is bracket-aware, so an ATT can
  // sit wholly inside it and must not be chipped a second time.
  { regex: TODO_REGEX, type: 'todo', className: 'annotation-chip annotation-chip-todo' },
  { regex: ATT_REGEX, type: 'attachment', className: 'annotation-chip annotation-chip-att' },
] as const

interface Chip {
  start: number
  end: number
  type: string
  className: string
}

function findChips(text: string): Chip[] {
  const chips: Chip[] = []
  for (const { regex, type, className } of PATTERNS) {
    // Shared /g regexes; matchAll copies their lastIndex.
    regex.lastIndex = 0
    for (const match of text.matchAll(regex)) {
      const start = match.index ?? 0
      const end = start + match[0].length
      if (chips.some((chip) => start < chip.end && end > chip.start)) continue
      chips.push({ start, end, type, className })
    }
  }
  return chips.sort((a, b) => a.start - b.start)
}

function wrapChipsInTextNode(node: Text): void {
  const text = node.data
  const chips = findChips(text)
  if (chips.length === 0) return

  const doc = node.ownerDocument
  const fragment = doc.createDocumentFragment()
  let cursor = 0
  for (const { start, end, type, className } of chips) {
    if (start > cursor) fragment.append(text.slice(cursor, start))
    const chip = doc.createElement('span')
    chip.className = className
    chip.setAttribute('data-annotation-type', type)
    chip.textContent = text.slice(start, end)
    fragment.append(chip)
    cursor = end
  }
  if (cursor < text.length) fragment.append(text.slice(cursor))
  node.replaceWith(fragment)
}

/** Wrap every to-do in `html` in the editor's chip. */
export function decorateToDos(html: string): string {
  if (!html || !/\[(?:TODO|ATT:)/.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  // Collect first: replacing a node mid-walk would derail the walker.
  const textNodes: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text)
  }
  textNodes.forEach(wrapChipsInTextNode)
  return doc.body.innerHTML
}
